/**
 * Compute estimates of editor commands (`EditorCommand.cost`), charged
 * against `AGENT_BUDGETS.pixelOpsPerTransaction` for agents and add-ons.
 *
 * One model for every command: cost = pixels a command loops over × the
 * work per pixel, in pixel operations. One pixel operation is one step of a
 * typed-array pass over `ImageData` (a few nanoseconds; High Pass measures
 * about 16 of them per pixel). Copies into and out of `ImageData` count as
 * passes. Native loops over tile stores (fills, masks, brush dabs) cost far
 * more per pixel: each maps the pixel to document space, tests it against
 * the pixel selection with a linear scan of its rectangles, finds the tile
 * and blends, measured at about 45 typed-array steps plus one per selection
 * rectangle (`COST_MODEL`).
 *
 * A layer the document does not have yet (an alias created by an earlier
 * intent of the transaction) counts as the canvas. Estimates are upper
 * bounds: they ignore clipping by the selection and by the layer bounds
 * wherever that would need the pixels themselves.
 */
import type { LayerId, Ref } from '@maipo/agent-protocol';
import type { Matrix } from '../../core/geometry';
import { findLayer, layerDocumentTransform, type EditorDocument, type Layer } from '../../core/model';
import { isAlias } from '../intents/common';

export const COST_MODEL = Object.freeze({
  /** One pixel of a native tile-store loop (transform, selection test, tile lookup, blend). */
  nativePixel: 48,
  /** Each rectangle of the selection that loop scans per pixel. */
  regionTest: 1,
});

const EMPTY_ALIASES: ReadonlyMap<string, LayerId> = new Map();

/** The layer `ref` names now, `undefined` when it does not exist yet (or at all). */
export function costLayer(document: EditorDocument, ref: Ref | undefined, aliases: ReadonlyMap<string, LayerId> = EMPTY_ALIASES): Layer | undefined {
  if (ref === undefined) return undefined;
  const id = isAlias(ref) ? aliases.get(ref) : ref;
  return id === undefined ? undefined : findLayer(document.root, id) ?? undefined;
}

export function canvasPixels(document: EditorDocument): number {
  return Math.max(0, document.width) * Math.max(0, document.height);
}

/** Raster pixels of `ref`: its raster area, 0 for a layer without pixels, the canvas for a layer not created yet. */
export function rasterPixels(document: EditorDocument, ref: Ref | undefined, aliases?: ReadonlyMap<string, LayerId>): number {
  const layer = costLayer(document, ref, aliases);
  if (!layer) return canvasPixels(document);
  return layer.kind === 'raster' ? layer.raster.width * layer.raster.height : 0;
}

/** Pixel operations of one pixel of a native tile-store loop under `selection` (none: no rectangles to scan). */
export function nativePixelCost(selection: { regions: readonly unknown[]; inverted?: boolean } | null | undefined): number {
  if (!selection || (selection.regions.length === 0 && !selection.inverted)) return COST_MODEL.nativePixel;
  return COST_MODEL.nativePixel + selection.regions.length * COST_MODEL.regionTest;
}

/**
 * Largest stretch of `matrix`'s inverse: layer pixels per document pixel along
 * the worst direction (the spectral norm of the inverse linear part). A
 * stroke's dab count scales with it, since strokes space dabs in layer pixels.
 */
export function inverseStretch(matrix: Matrix | null | undefined): number {
  if (!matrix) return 1;
  const [a, b, c, d] = matrix;
  const determinant = a * d - b * c;
  if (!Number.isFinite(determinant) || Math.abs(determinant) < 1e-12) return Number.POSITIVE_INFINITY;
  // Singular values of M: σ² = (F² ± sqrt(F⁴ − 4 det²)) / 2; the inverse's largest is 1 / σmin.
  const frobenius = a * a + b * b + c * c + d * d;
  const smallest = Math.sqrt(Math.max(0, (frobenius - Math.sqrt(Math.max(0, frobenius * frobenius - 4 * determinant * determinant))) / 2));
  return smallest > 0 ? 1 / smallest : Number.POSITIVE_INFINITY;
}

export function layerInverseStretch(document: EditorDocument, layer: Layer): number {
  return inverseStretch(layerDocumentTransform(document.root, layer.id) ?? layer.transform);
}

/** Finite, non-negative cost (an invalid estimate counts as over any budget). */
export function finiteCost(value: number): number {
  return Number.isFinite(value) && value >= 0 ? Math.ceil(value) : Number.MAX_SAFE_INTEGER;
}
